/**
 * Regras da tela do Pós-venda na Visão Geral (#6503): Dhaiane.
 *
 * Pergunta da tela: quais análises e clientes novos precisam de mim hoje?
 *
 * A análise cadastral anda em RODADAS. A rodada começa quando o termo é assinado, ou
 * quando a negociação volta para a fila ("reenviou a negociação para análise cadastral",
 * depois que o cliente manda o documento, ou "Pedido de Reanálise"), e termina com a
 * decisão registrada ("Análise Cadastral Concluída:").
 *
 * O prazo de 2 dias úteis vale só para a PRIMEIRA rodada (pedido do Felipe, 03/11/2026,
 * regra em src/lib/validacaoDados.ts): depois da primeira decisão, nem a espera pelo
 * documento nem a reanálise contam contra a analista. Reanálise aparece na fila sem
 * prazo, e a decisão dela fica fora da taxa "no prazo".
 *
 * O banco guarda só a última decisão em `analise_cadastral`; o histórico das rodadas sai
 * de `negociacao_logs`.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/posVenda.test.ts
 */
import { diasUteisEntre } from '../reguaPrazosAgrupamento.ts';
import { somarDiasUteis } from '../previsaoFaturaEquatorial.ts';
import { jaTeveAPrimeiraAnalise, naFilaDaValidacao } from '../validacaoDados.ts';

export const DU_PRAZO_ANALISE = 2;
export const META_ANALISES_NO_PRAZO = 0.9;
export const ACAO_DECISAO = 'Validação de Dados Concluída:';
/** #6526: a etapa passou a se chamar "Validação de Dados"; o histórico antigo continua com o nome anterior. */
export const ACOES_DECISAO = [ACAO_DECISAO, 'Análise Cadastral Concluída:'] as const;
export const ACOES_REINICIO = ['reenviou a negociação para validação de dados', 'reenviou a negociação para análise cadastral', 'Pedido de Reanálise:'] as const;
/** Decisões que deixam a negociação esperando o cliente (documento ou garantia). */
export const DECISOES_ESPERANDO_CLIENTE = ['Pendência Documental', 'Exigir Garantia'] as const;

/**
 * Dia (local, 'AAAA-MM-DD') de um instante. Datas antigas de assinatura foram gravadas como
 * meia-noite UTC; nesse caso vale a própria data (mesma correção de `getCorrectStartDate`).
 */
export function diaDoInstante(iso: string): string {
  if (/T00:00:00(\.0+)?(Z|\+00:00)$/.test(iso)) return iso.slice(0, 10);
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface RegistroDaAnalise {
  negociacao_id: number;
  acao: string;
  detalhes: string | null;
  created_at: string;
  user_id: string | null;
}

const ehReinicio = (r: RegistroDaAnalise) => (ACOES_REINICIO as readonly string[]).includes(r.acao);
const ehDecisao = (r: RegistroDaAnalise) => (ACOES_DECISAO as readonly string[]).includes(r.acao);

/** Início da rodada em curso num instante: o último reinício antes dele, ou a assinatura. */
export function inicioDaRodada(assinatura: string | null, registros: readonly RegistroDaAnalise[], antesDe: string): string | null {
  const reinicios = registros.filter(r => ehReinicio(r) && r.created_at < antesDe).map(r => r.created_at).sort();
  const ultimo = reinicios[reinicios.length - 1] ?? null;
  if (!assinatura) return ultimo;
  if (!ultimo) return assinatura;
  return new Date(ultimo).getTime() > new Date(assinatura).getTime() ? ultimo : assinatura;
}

/** Último dia para decidir uma rodada que começou em `inicio`. */
export const prazoDaRodada = (inicio: string): string => somarDiasUteis(diaDoInstante(inicio), DU_PRAZO_ANALISE);

// ─────────────────────────── Fila de hoje ───────────────────────────

export interface NegociacaoDaFila {
  id: number;
  cliente_nome: string | null;
  consumo_medio_kwh: number | string | null;
  status: string;
  data_assinatura_termo: string | null;
  decisao_final: string | null;
  decisao_final_at: string | null;
  /** Tempo da primeira análise, gravado na primeira decisão: preenchido = reanálise. */
  analise_cadastral_minutos_realizados?: number | null;
}

export interface ParaDecidir {
  negociacao: NegociacaoDaFila;
  inicio: string | null;
  prazo: string | null;
  /** Dias úteis desde o início da rodada (sem contar o dia de partida). */
  diasUteis: number | null;
  atrasada: boolean;
  /** Voltou para a fila (registro de reenvio ou pedido de reanálise). */
  reenviada: boolean;
  /** Já teve a primeira decisão: reanálise, sem prazo (`prazo` null, nunca atrasada). */
  reanalise: boolean;
}

export interface EsperandoCliente {
  negociacao: NegociacaoDaFila;
  motivo: 'documento' | 'garantia';
  desde: string | null;
  dias: number | null;
}

const diasCorridos = (de: string, hojeIso: string) => {
  const [a1, m1, d1] = de.split('-').map(Number);
  const [a2, m2, d2] = hojeIso.split('-').map(Number);
  return Math.round((new Date(a2, m2 - 1, d2).getTime() - new Date(a1, m1 - 1, d1).getTime()) / 86_400_000);
};

/**
 * A fila da análise agora. Para decidir = termo assinado esperando a análise (status
 * "termo_assinado", o "Novo" da tela de Gestão), com o prazo só na primeira análise.
 * Esperando o cliente = em análise com a última decisão pedindo documento ou garantia.
 */
export function filaDaAnalise(
  negociacoes: readonly NegociacaoDaFila[],
  registros: readonly RegistroDaAnalise[],
  hojeIso: string,
): { paraDecidir: ParaDecidir[]; atrasadas: number; esperandoCliente: EsperandoCliente[] } {
  const porNegociacao = new Map<number, RegistroDaAnalise[]>();
  for (const r of registros) {
    if (!porNegociacao.has(r.negociacao_id)) porNegociacao.set(r.negociacao_id, []);
    porNegociacao.get(r.negociacao_id)!.push(r);
  }
  const agora = '9999-12-31T00:00:00Z';

  const paraDecidir: ParaDecidir[] = negociacoes
    .filter(n => naFilaDaValidacao(n.status))
    .map(negociacao => {
      const regs = porNegociacao.get(negociacao.id) ?? [];
      const inicio = inicioDaRodada(negociacao.data_assinatura_termo, regs, agora);
      const reenviada = regs.some(ehReinicio);
      const reanalise = reenviada || jaTeveAPrimeiraAnalise(negociacao.analise_cadastral_minutos_realizados);
      const prazo = inicio && !reanalise ? prazoDaRodada(inicio) : null;
      return {
        negociacao,
        inicio,
        prazo,
        diasUteis: inicio ? diasUteisEntre(diaDoInstante(inicio), hojeIso) : null,
        atrasada: !!prazo && hojeIso > prazo,
        reenviada,
        reanalise,
      };
    })
    .sort((a, b) => (a.prazo ?? '9999').localeCompare(b.prazo ?? '9999'));

  const esperandoCliente: EsperandoCliente[] = negociacoes
    .filter(n => n.status === 'termo_analise' && (DECISOES_ESPERANDO_CLIENTE as readonly string[]).includes(n.decisao_final ?? ''))
    .map(negociacao => {
      const desde = negociacao.decisao_final_at ? diaDoInstante(negociacao.decisao_final_at) : null;
      return {
        negociacao,
        motivo: negociacao.decisao_final === 'Exigir Garantia' ? 'garantia' as const : 'documento' as const,
        desde,
        dias: desde ? diasCorridos(desde, hojeIso) : null,
      };
    })
    .sort((a, b) => (b.dias ?? 0) - (a.dias ?? 0));

  return { paraDecidir, atrasadas: paraDecidir.filter(p => p.atrasada).length, esperandoCliente };
}

// ─────────────────────────── Análises do mês ───────────────────────────

export interface AnalisesDoMes {
  /** Decisões da pessoa no mês (cada rodada decidida conta uma vez). */
  decisoes: number;
  /** Primeiras análises decididas no prazo / fora dele: só elas entram na taxa. */
  noPrazo: number;
  foraDoPrazo: number;
  /** Decisões depois da primeira (reanálise ou nova decisão): o prazo não vale, ficam de fora. */
  reanalises: number;
  /** Primeira análise sem data de início (sem assinatura nem reenvio): sem como medir. */
  semComoMedir: number;
  taxa: number | null;
  /** Pela última decisão de cada negociação no mês. */
  aprovadas: number;
  reprovadas: number;
  pendencia: number;
  garantia: number;
}

const grupoDaDecisao = (d: string | null): 'aprovadas' | 'reprovadas' | 'pendencia' | 'garantia' | null => {
  if (!d) return null;
  if (d.startsWith('Aprovado')) return 'aprovadas';
  if (d === 'Reprovado') return 'reprovadas';
  if (d === 'Pendência Documental') return 'pendencia';
  if (d === 'Exigir Garantia') return 'garantia';
  return null;
};

/**
 * Decisões da pessoa entre `inicio` e `fim` (ISO), cada uma contra o prazo da rodada.
 * `assinaturas` traz a data de assinatura de cada negociação; `registros`, o histórico
 * completo delas (decisões e reinícios de qualquer pessoa).
 */
export function analisesDoMes(
  registros: readonly RegistroDaAnalise[],
  assinaturas: ReadonlyMap<number, string | null>,
  pessoaId: string,
  inicio: string,
  fim: string,
): AnalisesDoMes {
  const porNegociacao = new Map<number, RegistroDaAnalise[]>();
  for (const r of registros) {
    if (!porNegociacao.has(r.negociacao_id)) porNegociacao.set(r.negociacao_id, []);
    porNegociacao.get(r.negociacao_id)!.push(r);
  }
  const t0 = new Date(inicio).getTime();
  const t1 = new Date(fim).getTime();
  const r: AnalisesDoMes = { decisoes: 0, noPrazo: 0, foraDoPrazo: 0, reanalises: 0, semComoMedir: 0, taxa: null, aprovadas: 0, reprovadas: 0, pendencia: 0, garantia: 0 };
  const ultimaNoMes = new Map<number, RegistroDaAnalise>();

  for (const [negId, regs] of porNegociacao) {
    for (const d of regs) {
      if (!ehDecisao(d) || d.user_id !== pessoaId) continue;
      const t = new Date(d.created_at).getTime();
      if (t < t0 || t >= t1) continue;
      r.decisoes++;
      const atual = ultimaNoMes.get(negId);
      if (!atual || d.created_at > atual.created_at) ultimaNoMes.set(negId, d);

      // Só a primeira decisão da negociação conta no prazo; as seguintes são reanálise.
      const primeira = !regs.some(o => ehDecisao(o) && o.created_at < d.created_at);
      if (!primeira) { r.reanalises++; continue; }
      const ini = inicioDaRodada(assinaturas.get(negId) ?? null, regs, d.created_at);
      if (!ini) { r.semComoMedir++; continue; }
      if (diaDoInstante(d.created_at) <= prazoDaRodada(ini)) r.noPrazo++;
      else r.foraDoPrazo++;
    }
  }
  for (const d of ultimaNoMes.values()) {
    const g = grupoDaDecisao(d.detalhes);
    if (g) r[g]++;
  }
  const medidas = r.noPrazo + r.foraDoPrazo;
  r.taxa = medidas ? r.noPrazo / medidas : null;
  return r;
}
